/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import ButtonView, {
  ButtonViewType,
  ButtonViewState,
} from '@airasia-phoenix/react-native/components/ButtonView';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import HeaderView, {
  HeaderStyle,
} from '@airasia-phoenix/react-native/widgets/HeaderView';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';
import { format } from 'date-fns';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  ComponentProps,
  ComponentType,
  useRef,
} from 'react';

//react native imports
import { useLogger } from '@airasia-rn/core-logger';
import { StationTypeFilter, useStationsMap } from '@airasia-rn/purchases';
import {
  SUPPORTED_LOCALES,
  type SupportedLocales,
  useAppConfig,
  useDeviceInfo,
} from '@airasia-rn/ui';
import { useTranslation } from 'react-i18next';
import {
  SafeAreaView,
  ScrollView,
  View,
  useWindowDimensions,
  Platform,
  NativeModules,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import {
  apiErrorMeta,
  errorTrackingEvent,
  FlightSearchEvent,
  FlightSearchMeta,
} from '../../analytics';
import {
  FlightDealsCarouselView,
  OriginDestinationSelectorView,
  RecentSearchesCarouselView,
  REWARDS_CURRENCY,
} from '../../components';
import { CalendarView } from '../../components/Calendar';
import { CheckboxView } from '../../components/CheckboxView';
import { isEmptyObject } from '../../components/OriginDestinationSelector/utils';
import PassengerClassSelectionConstants from '../../components/PassengerClass/constants';
import PassengerClassFieldView from '../../components/PassengerClass/PassengerClassFieldView';
import {
  CabinClassType,
  SelectedValues,
} from '../../components/PassengerClass/types';
import { PromoCodeView } from '../../components/PromoCodeTextField';
import { TripTypeSelectorView } from '../../components/TripTypeSelector';
import { JOURNEY_DATE_CONFIG, PROVIDERS } from '../../config/constants';
import { SCREEN_NAME } from '../../config/analytics';
import { useSuperAppContext } from '../../context/SuperAppContext';
import ERROR_FIELD from '../../error';
import {
  useSearchFlightsFiltersMutation,
  useSearchFlightsFilters,
} from '../../hooks/flights';
import { useQueryClient } from '@tanstack/react-query';
import { clearCheckoutAncillarySelections } from '../../hooks/useCheckoutAncillaryReset';
import {
  useAddRecentSearchMutation,
  useRecentSearches,
  recentSearchesKey,
  flattenRecentSearchesForHistory,
  type RecentSearchesStore,
} from '../../hooks/useRecentSearches';
import {
  useSearchAggregator,
  useSearchAggregatorSelector,
} from '../../hooks/useSearchAggregator';
import { StationDataObject, TripType } from '../../types/FlightSearch.types';
import {
  getCabinClassType,
  getJourneyDuration,
  getProvider,
  isDotrezFlight,
  isSameOriginAndDestination,
  parseDeepLinkDate,
  resolveNonExpiredDates,
  stationOrCityCodeOrName,
  stringToBool,
  snapQueryParams,
} from '../../util/FlightSearch.utils';
import { getDeepLinkCarrierFilterOverrides } from '../../util/FlightListing.utils';
import Constants from './constants';
import FlightSearchWidgetProps, { FlightDeepLinkParams } from './types';
import { superAppModules } from '../../config/superAppModules';
import { useStationSearch } from '../../hooks/useStationSearch';
import { QueryType } from '../../types/FlightSearch.types';
import { resolveStation } from '../../util/FlightSearch.utils';
import { defaultSearchFlightFilters } from '../../hooks/flights';
import { buildListingFilterResetPatch } from '../../util/FlightFilters.utils';
import { formatDateDDMMYYYY } from '../../util/utils';
import CalendarConstants from '../../components/Calendar/constants';
import {
  useFlightConfig,
  type FlightConfig,
} from '../../config/FlightConfigProvider';
import { useMultiCartContext } from '../../hooks/useMultiCartContext';

import ArrowBack from '@move-travel/react-native-phoenix-icons/ArrowBack';
import ArrowBackIos from '@move-travel/react-native-phoenix-icons/ArrowBackIos';

const isDistressflights = (currency?: string): boolean => {
  return currency?.toUpperCase() === REWARDS_CURRENCY[1];
};

/**
 * A rewards deep link carries the product code and the rewards channel hash
 * (iOS/Android each have their own), optionally without stations - departure
 * and arrival are filled from pass-history / user input instead.
 */
const isRewardsDeepLink = (
  params?: Pick<
    FlightDeepLinkParams,
    'productCode' | 'channelHash' | 'currency'
  > | null,
): boolean => {
  return (
    Boolean(params?.productCode) &&
    Boolean(params?.channelHash) &&
    isDistressflights(params?.currency)
  );
};

const ArrowBackPlatform = Platform.OS === 'ios' ? ArrowBackIos : ArrowBack;
const MARGIN_TOP = 13;
const MARGIN_RIGHT = 26;

const VALID_HIDE_KEYS = [
  'banner',
  'promo',
  'snap',
  'recentSearch',
  'flightRecommendation',
];
const VALID_DISABLE_KEYS = [
  'departureStation',
  'arrivalStation',
  'airasiaFlightOnly',
];
const DEFAULT_LOCALE: SupportedLocales = 'en-gb';
const UHF_LOCALE_COOKIE_NAME = 'uhfLocale';

const normalizeSupportedLocale = (
  locale?: string,
): SupportedLocales | undefined => {
  if (!locale) {
    return undefined;
  }

  const normalizedLocale = locale.trim().replace('_', '-').toLowerCase();
  return SUPPORTED_LOCALES.includes(normalizedLocale as SupportedLocales)
    ? (normalizedLocale as SupportedLocales)
    : undefined;
};

const getUhfLocaleFromCookie = (): SupportedLocales | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookieValue = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${UHF_LOCALE_COOKIE_NAME}=`))
    ?.split('=')[1];

  return normalizeSupportedLocale(
    cookieValue ? decodeURIComponent(cookieValue) : undefined,
  );
};

const resolveSearchLocale = (
  deepLinkLocale?: string,
  appLocale?: string,
): SupportedLocales =>
  normalizeSupportedLocale(deepLinkLocale) ??
  getUhfLocaleFromCookie() ??
  normalizeSupportedLocale(appLocale) ??
  DEFAULT_LOCALE;

const parseTripTypeFromParam = (tripType?: string): TripType | undefined => {
  const normalizedTripType = tripType?.replace(/_/g, '').toUpperCase();

  if (normalizedTripType === 'R' || normalizedTripType === 'ROUNDTRIP') {
    return TripType.ROUND_TRIP;
  }
  if (normalizedTripType === 'O' || normalizedTripType === 'ONEWAY') {
    return TripType.ONE_WAY;
  }

  return undefined;
};

const resolveTripTypeFromParam = (tripType?: string): TripType =>
  parseTripTypeFromParam(tripType) ?? TripType.ONE_WAY;

const getRecentSearchMetaKey = (
  currentMeta?: RecentSearchesStore['currentMetaSearch'],
): string | undefined => {
  if (!currentMeta) return undefined;

  return JSON.stringify({
    tripType: currentMeta.tripType,
    departDate: currentMeta.departDate
      ? String(currentMeta.departDate)
      : undefined,
    returnDate: currentMeta.returnDate
      ? String(currentMeta.returnDate)
      : undefined,
    passengers: currentMeta.passengers,
    cabinClass: currentMeta.cabinClass,
  });
};

const formatSearchEventCabinClass = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const buildSearchEventLabel = ({
  originObj,
  destinationObj,
  departDate,
  returnDate,
  tripType,
  adultCount,
  childCount,
  infantCount,
  cabinClass,
  promoCode,
  analyticsConstants,
}: {
  originObj?: StationDataObject;
  destinationObj?: StationDataObject;
  departDate?: Date | null;
  returnDate?: Date | null;
  tripType: TripType;
  adultCount: number;
  childCount: number;
  infantCount: number;
  cabinClass: string;
  promoCode?: string;
  analyticsConstants: typeof FlightSearchMeta.ANALYTICS_CONSTANTS;
}) => {
  const originCode = originObj
    ? stationOrCityCodeOrName(originObj, 'code')
    : '';
  const destinationCode = destinationObj
    ? stationOrCityCodeOrName(destinationObj, 'code')
    : '';
  const airportSelection =
    originObj?.IsCity || destinationObj?.IsCity
      ? analyticsConstants.CITY
      : analyticsConstants.AIRPORT;
  const inventory =
    destinationObj && getProvider(destinationObj) === PROVIDERS.AAB
      ? analyticsConstants.AA
      : analyticsConstants.OTA;
  const formattedDepartDate = departDate
    ? format(departDate, analyticsConstants.DATE_FORMAT)
    : '';
  const formattedReturnDate = returnDate
    ? format(returnDate, analyticsConstants.DATE_FORMAT)
    : '';
  const formattedTripType =
    tripType === TripType.ROUND_TRIP
      ? analyticsConstants.RT
      : analyticsConstants.OW;
  const formattedPromo = promoCode?.trim() ?? '';

  return `${originCode} - ${destinationCode}(${airportSelection})|${formattedDepartDate} - ${formattedReturnDate}|${inventory}|ADL=${adultCount},CHD=${childCount},INF=${infantCount}|${formattedTripType}|${formatSearchEventCabinClass(cabinClass)}|${formattedPromo}`;
};

const FlightSearchWidget = (props: FlightSearchWidgetProps) => {
  const { width } = useWindowDimensions();
  const initialTripType = resolveTripTypeFromParam(
    (props.controls.deepLinkParams as FlightDeepLinkParams | undefined)
      ?.tripType,
  );
  const [tripType, setTripType] = useState<TripType>(() => initialTripType);
  const [departDate, setDepartDate] = useState<Date | undefined>(
    JOURNEY_DATE_CONFIG.departDate,
  );
  const [returnDate, setReturnDate] = useState<Date | undefined>(() =>
    initialTripType === TripType.ROUND_TRIP
      ? JOURNEY_DATE_CONFIG.returnDate
      : undefined,
  );
  const updateTripType = useCallback((nextTripType: TripType) => {
    const normalizedTripType = resolveTripTypeFromParam(nextTripType);

    setTripType(normalizedTripType);
    if (normalizedTripType === TripType.ONE_WAY) {
      setReturnDate(undefined);
    }
  }, []);
  const { t } = useTranslation('flights');
  const { logEvent, logError } = useLogger();
  const { config } = useAppConfig();
  const { config: flightConfig, updateConfig } = useFlightConfig();
  const isDistress = !!flightConfig?.isDistressflights;
  const { updateMultiCartContext, clearMultiCartContext } =
    useMultiCartContext();

  const isSmallScreen = getCurrentBreakpoint() < 3;

  const [promoCode, setPromoCode] = useState<string | undefined>(undefined);
  const [isSnapCheckbox, setIsSnapCheckbox] = useState(false);
  const [isAirAsiaFlightsOnly, setIsAirAsiaFlightsOnly] = useState(false);

  const [originObj, setOriginObj] = useState<StationDataObject | undefined>(
    undefined,
  );
  const [destinationObj, setDestinationObj] = useState<
    StationDataObject | undefined
  >(undefined);
  const [isOriginFieldError, setIsOriginFieldError] = useState<
    ERROR_FIELD | undefined
  >(undefined);
  const [isDestinationFieldError, setIsDestinationFieldError] = useState<
    ERROR_FIELD | undefined
  >(undefined);
  const [adultCount, setAdultCount] = useState(
    PassengerClassSelectionConstants.DEFAULT_ADULT_COUNT,
  );
  const [childCount, setChildCount] = useState(
    PassengerClassSelectionConstants.DEFAULT_CHILD_COUNT,
  );
  const [infantCount, setInfantCount] = useState(
    PassengerClassSelectionConstants.DEFAULT_INFANT_COUNT,
  );
  const [cabinClass, setCabinClass] = useState<CabinClassType>(
    CabinClassType.ECONOMY,
  );
  const [uiHide, setUiHide] = useState<Record<string, boolean>>({});
  const [uiDisable, setUiDisable] = useState<Record<string, boolean>>({});

  const [isFlightDealsSearchTriggered, setIsFlightDealsSearchTriggered] =
    useState(false);
  const [isDeepLinkProcessed, setIsDeepLinkProcessed] = useState(false);
  const [didApplyDeepLink, setDidApplyDeepLink] = useState(false);
  const [didApplyEditSearch, setDidApplyEditSearch] = useState(false);
  // Latch that survives the generic deep link flag resets (deepLinkKey
  // changes, e.g. the host re-invoking the same rewards deep link without
  // its stations). Once a rewards deep link has been applied, the
  // pass-history prefill must never override it - it only fills gaps for
  // rewards links that were never applied (station-less rewards links).
  const appliedRewardsDeepLinkRef = useRef(false);
  const appliedRecentSearchMetaKeyRef = useRef<string | undefined>(undefined);

  const { mutateAsync: searchMutateAsync } = useSearchFlightsFiltersMutation();
  const searchAggregator = useSearchAggregator(SCREEN_NAME.flightSearchWidget);
  const addRecentSearch = useAddRecentSearchMutation();
  const queryClient = useQueryClient();
  const { data: filters } = useSearchFlightsFilters();
  const { backHandler, onSearchPress, openModule } = useSuperAppContext();
  const isListingScreen = props.controls?.isListingScreen ?? false;
  const isHomeScreen = props.controls?.isHomeScreen ?? false;
  // On the listing screen, trip type can still be corrected shortly after
  // mount (deep link resolution, recent-searches/filters prefill from the
  // persisted query cache restoring asynchronously). Hold the trip type
  // tabs hidden for a brief settle window so they never paint a value
  // that's about to change.
  const [isTripTypeSettled, setIsTripTypeSettled] = useState(!isListingScreen);
  const { isMobileWidth, isWeb } = useDeviceInfo();
  const ScrollContainer = (isMobileWidth ? ScrollView : View) as ComponentType<
    ComponentProps<typeof ScrollView>
  >;
  const [isSearchFlightsButtonLoading, setIsSearchFlightsButtonLoading] =
    useState<boolean>(false);
  const {
    funnel,
    searchAgg,
    isLoading: isSearchAggregatorLoading,
    isError: isSearchAggregatorError,
    isSuccess: isSearchAggregatorSuccess,
  } = useSearchAggregatorSelector();
  const [originQuery, setOriginQuery] = useState<QueryType | null>(null);
  const [destQuery, setDestQuery] = useState<QueryType | null>(null);
  const { data: originSearchResults, isLoading: isOriginSearching } =
    useStationSearch(originQuery, SCREEN_NAME.flightSearchWidget);
  const { data: destSearchResults, isLoading: isDestSearching } =
    useStationSearch(destQuery, SCREEN_NAME.flightSearchWidget);
  const [autoOpenDatePickerMenu, setAutoOpenDatePickerMenu] = useState(false);
  const [
    shouldAutoSearchOnCalendarConfirm,
    setShouldAutoSearchOnCalendarConfirm,
  ] = useState(false);

  // AirAsia-only comes from the search form checkbox, so it is not reset here
  const resetListingPopularFilters = useMemo(
    () =>
      buildListingFilterResetPatch(defaultSearchFlightFilters, {
        includeAirAsiaOnly: false,
      }),
    [],
  );

  useEffect(() => {
    const promoCodeFromDeepLink = props?.controls?.deepLinkParams?.promoCode;
    if (isHomeScreen && !!promoCodeFromDeepLink) {
      setPromoCode(promoCodeFromDeepLink);
    }
  }, [isHomeScreen, props.controls?.deepLinkParams?.promoCode]);

  const deepLinkAirlinesCarrierOverrides = useMemo(() => {
    const params = props.controls.deepLinkParams as
      | FlightDeepLinkParams
      | undefined;
    return getDeepLinkCarrierFilterOverrides({
      airlines: params?.airlines,
      isAirasiaFlightOnly:
        params?.isAirasiaFlightOnly || params?.airasiaFlightOnly,
    });
  }, [props.controls.deepLinkParams]);

  const deepLinkKey = useMemo(
    () => JSON.stringify(props.controls?.deepLinkParams ?? {}),
    [props.controls?.deepLinkParams],
  );

  const translations = useMemo(
    () => ({
      headerTitle: t('flightsSearchPage.main.flightSearch'),
      flightHotelCheckboxLabel: t(
        'flightsSearchPage.main.flightHotelSaverBundleLabel',
      ),
      flightHotelSaverBundleBadgeLabel: t(
        'flightsSearchPage.main.flightHotelSaverBundleBadgeLabel',
      ),
      airAsiaFlightsOnlyLabel: t(
        'flightsSearchPage.main.airAsiaFlightsOnlyLabel',
      ),
    }),
    [t],
  );

  useEffect(() => {
    logEvent(
      FlightSearchMeta.EVENT_NAMES.searchWidgetViewed,
      FlightSearchEvent.searchWidgetViewedEvent(),
    );
  }, []);

  useEffect(() => {
    if (tripType === TripType.ROUND_TRIP) {
      // Check if returnDate is undefined, null, or is scheduled before the departDate
      if (!returnDate || (departDate && returnDate < departDate)) {
        const baseDate = departDate || new Date();
        // CalendarConstants.DAYS_GAP_DEPART_RETURN_DATE returns seconds (e.g., 259200 for 3 days)
        const gapInMilliseconds =
          CalendarConstants.DAYS_GAP_DEPART_RETURN_DATE * 1000;
        const targetReturnDate = new Date(
          baseDate.getTime() + gapInMilliseconds,
        );

        setReturnDate(targetReturnDate);

        // Trigger auto-search immediately when auto-setting the date ───
        if (
          isListingScreen &&
          !isSmallScreen &&
          originObj &&
          !isEmptyObject(originObj) &&
          destinationObj &&
          !isEmptyObject(destinationObj)
        ) {
          setShouldAutoSearch(true);
        }
      }
    }
  }, [
    tripType,
    departDate,
    returnDate,
    isListingScreen,
    isSmallScreen,
    originObj,
    destinationObj,
  ]);

  const { data: recentSearchesStore } = useRecentSearches();

  const buildListingSearchUrl = useCallback(() => {
    if (
      Platform.OS !== 'web' ||
      typeof window === 'undefined' ||
      !originObj ||
      !destinationObj ||
      !departDate
    ) {
      return undefined;
    }

    const existingSearchParams = new URLSearchParams(window.location.search);
    const funnelType = existingSearchParams.get('type');
    const deepLinkLocale = (
      props.controls.deepLinkParams as FlightDeepLinkParams | undefined
    )?.locale;

    const paramsObj: Record<string, string | undefined> = {
      origin: stationOrCityCodeOrName(originObj as StationDataObject, 'code'),
      destination: stationOrCityCodeOrName(
        destinationObj as StationDataObject,
        'code',
      ),
      departDate: formatDateDDMMYYYY(departDate),
      tripType: tripType === TripType.ROUND_TRIP ? 'R' : 'O',
      adult: String(adultCount),
      child: String(childCount),
      infant: String(infantCount),
      locale: resolveSearchLocale(deepLinkLocale, config.locale),
      currency: flightConfig?.currency || config?.currency || 'MYR',
      ule: String(Boolean(funnel?.enableULE)),
      cabinClass: cabinClass.toLowerCase(),
      uce: String(Boolean(funnel?.enableUCE)),
      ancillaryAbTest: String(Boolean(funnel?.enableAncillaryAbTest)),
      isOC: String(Boolean(originObj.IsCity)),
      isDC: String(Boolean(destinationObj.IsCity)),
      promoCode: promoCode?.trim() || undefined,
      type: funnel?.type?.trim() || funnelType || undefined,
      airlineProfile: funnel?.airlineProfile?.trim() || undefined,
      upsellWidget: String(Boolean(funnel?.enableSnapUpsell)),
      upsellPremiumFlatbedWidget: String(
        Boolean(funnel?.enablePremiumFlatbedUpsell),
      ),
      upsellFlightBusWidget: String(Boolean(funnel?.enableFlightBusUpsell)),
      providers: funnel?.provider?.trim() || undefined,
      returnDate:
        tripType === TripType.ROUND_TRIP && returnDate
          ? formatDateDDMMYYYY(returnDate)
          : undefined,
    };

    const filteredParamsObj = Object.entries(paramsObj).reduce<
      Record<string, string>
    >((acc, [key, value]) => {
      if (value !== '' && value != null) {
        acc[key] = value;
      }

      return acc;
    }, {});

    const searchParams = new URLSearchParams(filteredParamsObj);

    return `${window.location.pathname}?${searchParams.toString()}`;
  }, [
    originObj,
    destinationObj,
    departDate,
    tripType,
    adultCount,
    childCount,
    infantCount,
    props.controls.deepLinkParams,
    config.locale,
    config.currency,
    flightConfig.currency,
    funnel?.enableULE,
    funnel?.enableUCE,
    funnel?.enableAncillaryAbTest,
    funnel?.type,
    funnel?.airlineProfile,
    funnel?.enableSnapUpsell,
    funnel?.enablePremiumFlatbedUpsell,
    funnel?.enableFlightBusUpsell,
    funnel?.provider,
    cabinClass,
    promoCode,
    returnDate,
  ]);

  useEffect(() => {
    const currentMeta = recentSearchesStore?.currentMetaSearch;
    const currentMetaKey = getRecentSearchMetaKey(currentMeta);

    if (didApplyEditSearch) return;
    // Once a rewards deep link has been applied (stations resolved), the
    // pass-history prefill must never override it - even if the host later
    // re-invokes the same deep link without its stations, which resets the
    // generic didApplyDeepLink flag above. Station-less rewards links are
    // unaffected: the latch is only set by the station-resolved apply path.
    if (
      isRewardsDeepLink(props.controls?.deepLinkParams) &&
      appliedRewardsDeepLinkRef.current
    ) {
      return;
    }
    // We only prefill if we aren't currently processing a Deep Link
    // and we actually have stored metadata.
    if (isDeepLinkProcessed && !didApplyDeepLink && currentMeta) {
      if (currentMetaKey === appliedRecentSearchMetaKeyRef.current) return;

      appliedRecentSearchMetaKeyRef.current = currentMetaKey;

      // 1. Prefill Trip Type (Priority)
      const currentMetaTripType = parseTripTypeFromParam(currentMeta.tripType);

      if (currentMetaTripType) updateTripType(currentMetaTripType);

      // 2. Prefill Dates
      const recentSearchDates = resolveNonExpiredDates(
        currentMeta.departDate ? new Date(currentMeta.departDate) : undefined,
        currentMeta.returnDate ? new Date(currentMeta.returnDate) : undefined,
        CalendarConstants.DAYS_GAP_DEPART_RETURN_DATE,
      );
      if (recentSearchDates.departDate) {
        setDepartDate(recentSearchDates.departDate);
      }
      if (
        currentMetaTripType !== TripType.ONE_WAY &&
        recentSearchDates.returnDate
      ) {
        setReturnDate(recentSearchDates.returnDate);
      } else if (currentMetaTripType === TripType.ONE_WAY) {
        setReturnDate(undefined);
      }

      // 3. Prefill Passengers
      if (currentMeta.passengers) {
        const { adultCount, childCount, infantCount } = currentMeta.passengers;
        setAdultCount(
          adultCount ?? PassengerClassSelectionConstants.DEFAULT_ADULT_COUNT,
        );
        setChildCount(
          childCount ?? PassengerClassSelectionConstants.DEFAULT_CHILD_COUNT,
        );
        setInfantCount(
          infantCount ?? PassengerClassSelectionConstants.DEFAULT_INFANT_COUNT,
        );
      }

      // 4. Prefill Cabin Class
      if (currentMeta.cabinClass) {
        setCabinClass(currentMeta.cabinClass);
      }
    }
  }, [
    recentSearchesStore?.currentMetaSearch,
    isDeepLinkProcessed,
    didApplyDeepLink,
    didApplyEditSearch,
    props.controls?.deepLinkParams,
    updateTripType,
  ]);

  // Prefill
  useEffect(() => {
    if (!filters?.didEditSearch) return;

    const {
      tripType: filterTripType,
      originObj,
      destinationObj,
      departDate,
      returnDate,
      adultCount,
      infantCount,
      childCount,
      cabinClass,
      promoCode,
      isAirAsiaFlightsOnly,
    } = filters;
    const resolvedTripType = parseTripTypeFromParam(filterTripType);
    const nextTripType = resolvedTripType ?? tripType;
    if (resolvedTripType) updateTripType(resolvedTripType);
    setOriginObj(originObj);
    setDestinationObj(destinationObj);
    setDepartDate(departDate);
    setReturnDate(
      nextTripType === TripType.ROUND_TRIP ? returnDate : undefined,
    );
    setAdultCount(adultCount);
    setInfantCount(infantCount);
    setChildCount(childCount);
    setCabinClass(getCabinClassType(cabinClass));
    setPromoCode(promoCode ?? '');
    setIsAirAsiaFlightsOnly(isAirAsiaFlightsOnly);
    setDidApplyEditSearch(true);

    searchMutateAsync({ didEditSearch: false });
  }, [filters?.didEditSearch, tripType, updateTripType, searchMutateAsync]);

  // Give the deep link / recent-searches / filters prefills a brief window
  // to settle before revealing the trip type tabs, so they don't paint a
  // value that's about to be corrected.
  useEffect(() => {
    if (!isListingScreen || !isDeepLinkProcessed || isTripTypeSettled) return;

    const timeout = setTimeout(() => setIsTripTypeSettled(true), 300);
    return () => clearTimeout(timeout);
  }, [isListingScreen, isDeepLinkProcessed, isTripTypeSettled]);

  // Extract and persist multi-cart parameters
  useEffect(() => {
    const params = props.controls.deepLinkParams as FlightDeepLinkParams;
    if (!params) return;

    const cartId = params.cartId;
    const sourceLob = params.sourceLob || params.lob;
    const channelHash = params.channelHash || params.key;
    const sourceDeeplink = params.sourceDeeplink;
    const uiHide = params.uiHide;
    const uiDisable = params.uiDisable;
    const airasiaFlightOnly =
      params.airasiaFlightOnly || params.isAirasiaFlightOnly;

    if (cartId) {
      updateMultiCartContext({
        cartId,
        sourceLob,
        channelHash,
        sourceDeeplink,
        uiHide,
        uiDisable,
        airasiaFlightOnly,
      });
    } else if (Platform.OS !== 'web') {
      clearMultiCartContext();
    }
  }, [
    props.controls.deepLinkParams,
    clearMultiCartContext,
    updateMultiCartContext,
  ]);

  // Extract and persist configuration parameters (Distress Flights, Product Code, Channel Hash)
  useEffect(() => {
    const params = props.controls.deepLinkParams as FlightDeepLinkParams;
    if (!params) return;

    const channelHash = params.channelHash;
    const productCode = params.productCode;
    const isNative = Platform.OS !== 'web';

    const updates: Partial<FlightConfig> = {};

    if (productCode !== undefined) {
      updates.productCode = productCode;
    }

    if (channelHash) {
      updates.channelHash = channelHash;
    }

    if (isNative && isDistressflights(params.currency)) {
      updates.isDistressflights = true;
      updates.currency = 'LPX';
    } else if (params.cartId && params.currency) {
      updates.currency = params.currency.toUpperCase();
    }

    if (Object.keys(updates).length > 0) {
      updateConfig(updates);
    }
  }, [props.controls.deepLinkParams, updateConfig]);

  useEffect(() => {
    const isDeepLink =
      stringToBool(props.controls?.deepLinkParams?.isDeepLink) ||
      Boolean(props.controls?.deepLinkParams?.cartId) ||
      Boolean(props.controls?.deepLinkParams?.departureStation) ||
      isRewardsDeepLink(props.controls?.deepLinkParams);

    if (isDeepLink) {
      setIsDeepLinkProcessed(false);
      setDidApplyDeepLink(false);
      setOriginQuery(null);
      setDestQuery(null);
    }
  }, [deepLinkKey]);

  useEffect(() => {
    const params = props.controls.deepLinkParams as FlightDeepLinkParams;
    const isDeepLink =
      stringToBool(props.controls.deepLinkParams?.isDeepLink) ||
      Boolean(params?.cartId) ||
      Boolean(params?.departureStation) ||
      isRewardsDeepLink(params);

    if (!isDeepLink || isDeepLinkProcessed) {
      return;
    }

    const originCode = params.origin || params.departureStation;
    const destinationCode = params.destination || params.arrivalStation;
    if (!originCode || !destinationCode) {
      return;
    }
    setOriginQuery({
      query: originCode!,
      locale: params.locale || config.locale,
    });
    setDestQuery({
      query: destinationCode!,
      origin: originCode,
      locale: params.locale || config.locale,
    });
  }, [deepLinkKey, isDeepLinkProcessed, config.locale]);

  // UI Controls DeepLink
  useEffect(() => {
    const params = props.controls.deepLinkParams as FlightDeepLinkParams;
    if (!params) return;

    const parseToMap = (
      input: string | string[] | undefined,
      validKeys: string[],
    ) => {
      const items =
        typeof input === 'string'
          ? input.split(',')
          : Array.isArray(input)
            ? input
            : [];
      return items.reduce<Record<string, boolean>>((acc, curr) => {
        const key = curr.trim();
        if (key && validKeys.includes(key)) acc[key] = true;
        return acc;
      }, {});
    };

    setUiHide(params.uiHide ? parseToMap(params.uiHide, VALID_HIDE_KEYS) : {});

    setUiDisable(
      params.uiDisable ? parseToMap(params.uiDisable, VALID_DISABLE_KEYS) : {},
    );
  }, [props.controls.deepLinkParams]);

  // DeepLink
  useEffect(() => {
    const deepLinkParams = props.controls
      .deepLinkParams as FlightDeepLinkParams;
    const isDeepLink =
      stringToBool(props.controls.deepLinkParams?.isDeepLink) ||
      Boolean(deepLinkParams?.cartId) ||
      Boolean(deepLinkParams?.departureStation) ||
      isRewardsDeepLink(deepLinkParams);

    // If no deep link is present, mark as processed and exit
    if (!isDeepLink) {
      setIsDeepLinkProcessed(true);
      setDidApplyDeepLink(false);
      return;
    }

    const originStationOrCityCode =
      deepLinkParams.origin || deepLinkParams.departureStation;
    const destinationStationOrCityCode =
      deepLinkParams.destination || deepLinkParams.arrivalStation;

    // Rewards deep links (product code + channel hash) can omit both stations.
    // There is nothing to resolve against the station search, so mark the deep
    // link as processed and let the pass-history / URL-field prefills settle
    // for whatever the deep link does not provide.
    if (
      isRewardsDeepLink(deepLinkParams) &&
      (!originStationOrCityCode || !destinationStationOrCityCode)
    ) {
      setIsDeepLinkProcessed(true);
      setOriginQuery(null);
      setDestQuery(null);
      return;
    }

    if (
      isDeepLink &&
      !isOriginSearching &&
      !isDestSearching &&
      !isDeepLinkProcessed &&
      originSearchResults &&
      destSearchResults
    ) {
      const {
        departDate,
        returnDate,
        tripType,
        adult,
        child,
        infant,
        cabinClass,
        promoCode,
        isOC,
        isDC,
        isAirasiaFlightOnly,
        airasiaFlightOnly,
      } = deepLinkParams;

      const { departDate: parsedDepartDate, returnDate: parsedReturnDate } =
        resolveNonExpiredDates(
          parseDeepLinkDate(departDate as string),
          parseDeepLinkDate(returnDate as string),
          CalendarConstants.DAYS_GAP_DEPART_RETURN_DATE,
        );

      const deepLinkOriginObj = resolveStation(
        originSearchResults ?? [],
        originStationOrCityCode ?? '',
        stringToBool((isOC as string) ?? false),
      );
      const deepLinkDestinationObj = resolveStation(
        destSearchResults ?? [],
        destinationStationOrCityCode ?? '',
        stringToBool((isDC as string) ?? false),
      );

      if (!deepLinkOriginObj || !deepLinkDestinationObj) {
        // Invalid station/city code in deep link, abort processing.
        // Trip type was already derived from the deep link param at mount,
        // so there's nothing to roll back here.
        setIsDeepLinkProcessed(true);
        return;
      }

      const resolvedTripType = resolveTripTypeFromParam(tripType);
      updateTripType(resolvedTripType);
      // Set Depart and return Station
      setOriginObj({ ...deepLinkOriginObj } as StationDataObject);
      setDestinationObj({ ...deepLinkDestinationObj } as StationDataObject);

      // Set Depart and return date
      setDepartDate(parsedDepartDate);
      setReturnDate(
        resolvedTripType === TripType.ROUND_TRIP ? parsedReturnDate : undefined,
      );

      // Set Passenger
      setAdultCount(Number(adult ?? 1));
      setChildCount(Number(child ?? 0));
      setInfantCount(Number(infant ?? 0));
      setCabinClass(getCabinClassType(cabinClass));
      setPromoCode(promoCode);
      setIsSnapCheckbox(false);
      setIsAirAsiaFlightsOnly(
        stringToBool(isAirasiaFlightOnly || airasiaFlightOnly),
      );

      setDidApplyDeepLink(true);
      setIsDeepLinkProcessed(true);
      if (isRewardsDeepLink(deepLinkParams)) {
        appliedRewardsDeepLinkRef.current = true;
      }
      setOriginQuery(null);
      setDestQuery(null);
    }
  }, [
    deepLinkKey,
    isOriginSearching,
    isDestSearching,
    isDeepLinkProcessed,
    originSearchResults,
    destSearchResults,
    updateTripType,
  ]);

  // Rewards deep links without stations: the pass-history prefill above
  // covers what the URL omits, so anything the rewards URL explicitly
  // provides (dates, passengers, cabin class, trip type, promo) wins.
  // Declared after the pass-history prefill effect so it runs later in the
  // same commit and can override those values.
  useEffect(() => {
    const params = props.controls.deepLinkParams as FlightDeepLinkParams;
    const hasStations = Boolean(
      params.origin ||
        params.departureStation ||
        params.destination ||
        params.arrivalStation,
    );

    if (
      !isDeepLinkProcessed ||
      didApplyDeepLink ||
      !isRewardsDeepLink(params) ||
      hasStations
    ) {
      return;
    }

    let appliedUrlField = false;

    const resolvedUrlTripType = parseTripTypeFromParam(params.tripType);

    if (resolvedUrlTripType) {
      updateTripType(resolvedUrlTripType);
      appliedUrlField = true;
    }

    const rewardsUrlDates = resolveNonExpiredDates(
      parseDeepLinkDate(params.departDate as string),
      parseDeepLinkDate(params.returnDate as string),
      CalendarConstants.DAYS_GAP_DEPART_RETURN_DATE,
    );
    if (rewardsUrlDates.departDate) {
      setDepartDate(rewardsUrlDates.departDate);
      appliedUrlField = true;
    }
    if (
      resolvedUrlTripType !== TripType.ONE_WAY &&
      rewardsUrlDates.returnDate
    ) {
      setReturnDate(rewardsUrlDates.returnDate);
      appliedUrlField = true;
    }

    if (params.adult !== undefined) {
      setAdultCount(Number(params.adult));
      appliedUrlField = true;
    }
    if (params.child !== undefined) {
      setChildCount(Number(params.child));
      appliedUrlField = true;
    }
    if (params.infant !== undefined) {
      setInfantCount(Number(params.infant));
      appliedUrlField = true;
    }

    if (params.cabinClass) {
      setCabinClass(getCabinClassType(params.cabinClass));
      appliedUrlField = true;
    }

    if (params.promoCode !== undefined) {
      setPromoCode(params.promoCode);
      appliedUrlField = true;
    }

    if (params.isAirasiaFlightOnly || params.airasiaFlightOnly) {
      setIsAirAsiaFlightsOnly(
        stringToBool(
          (params.isAirasiaFlightOnly || params.airasiaFlightOnly) as
            | string
            | boolean,
        ),
      );
      appliedUrlField = true;
    }

    // Only seal off the pass-history prefill when the rewards URL actually
    // provided fields. A minimal rewards deep link (product code + channel
    // hash only) leaves didApplyDeepLink false so the persisted pass-history
    // meta can settle in whenever its async hydration completes.
    if (appliedUrlField) {
      setDidApplyDeepLink(true);
    }
  }, [deepLinkKey, isDeepLinkProcessed, didApplyDeepLink, updateTripType]);

  const sendSearchEvent = () => {
    if (!originObj && !destinationObj) return;

    const { ANALYTICS_CONSTANTS, EVENT_NAMES } = FlightSearchMeta;

    const params = {
      label: buildSearchEventLabel({
        originObj,
        destinationObj,
        departDate,
        returnDate: tripType === TripType.ROUND_TRIP ? returnDate : undefined,
        tripType,
        adultCount,
        childCount,
        infantCount,
        cabinClass,
        promoCode,
        analyticsConstants: ANALYTICS_CONSTANTS,
      }),
      category: `${ANALYTICS_CONSTANTS.FLIGHT_SEARCH_WIDGET}|${ANALYTICS_CONSTANTS.FLIGHTS}`,
      searchParams: {
        flighttype:
          tripType === TripType.ROUND_TRIP
            ? ANALYTICS_CONSTANTS.RT
            : ANALYTICS_CONSTANTS.OW,
        totalpax: adultCount + childCount + infantCount,
        adult: adultCount,
        kid: childCount,
        infant: infantCount,
        voucher: promoCode ?? '',
        journeytype:
          originObj?.CountryCode === destinationObj?.CountryCode
            ? ANALYTICS_CONSTANTS.DOMESTIC
            : ANALYTICS_CONSTANTS.INTERNATIONAL,
        departdate: departDate
          ? format(departDate, ANALYTICS_CONSTANTS.DATE_FORMAT)
          : '',
        returndate:
          tripType === TripType.ROUND_TRIP && returnDate
            ? format(returnDate, ANALYTICS_CONSTANTS.DATE_FORMAT)
            : '',
        origincountry: originObj?.CountryCode ?? '',
        destinationcountry: destinationObj?.CountryCode ?? '',
        originairport: originObj
          ? stationOrCityCodeOrName(originObj, 'code')
          : '',
        destinationairport: destinationObj
          ? stationOrCityCodeOrName(destinationObj, 'code')
          : '',
        lengthofstay:
          tripType === TripType.ROUND_TRIP
            ? getJourneyDuration(returnDate, departDate)
            : 0,
        currency: flightConfig?.currency || config?.currency,
      },
    };

    logEvent(EVENT_NAMES.search, FlightSearchEvent.searchEvent(params));

    const { ANALYTICS_CONSTANTS: AC, YN } = FlightSearchMeta;
    const searchClickPayload = FlightSearchEvent.flightSearchClickEvent({
      originairport: originObj
        ? stationOrCityCodeOrName(originObj, 'code')
        : '',
      destinationairport: destinationObj
        ? stationOrCityCodeOrName(destinationObj, 'code')
        : '',
      origincountry: originObj?.CountryCode ?? '',
      destinationcountry: destinationObj?.CountryCode ?? '',
      departdate: departDate ? format(departDate, AC.DATE_FORMAT) : '',
      returndate:
        tripType === TripType.ROUND_TRIP && returnDate
          ? format(returnDate, AC.DATE_FORMAT)
          : '',
      journeytype:
        originObj?.CountryCode === destinationObj?.CountryCode
          ? AC.DOMESTIC
          : AC.INTERNATIONAL,
      lengthofstay:
        tripType === TripType.ROUND_TRIP
          ? getJourneyDuration(returnDate, departDate)
          : 0,
      routetype: null,
      flightcode: null,
      flighttype: tripType === TripType.ROUND_TRIP ? AC.RT : AC.OW,
      adult: adultCount,
      kid: childCount,
      infant: infantCount,
      totalpax: adultCount + childCount + infantCount,
      coupon: promoCode ?? '',
      fareclass: cabinClass.toLowerCase(),
      bigpoints: YN.N,
      snapbundle: isSnapCheckbox ? YN.Y : YN.N,
    });
    logEvent(
      FlightSearchMeta.EVENTS.productImpressionSearch,
      searchClickPayload,
    );
  };

  const handleSearch = async () => {
    try {
      if (isSameOriginAndDestination(originObj, destinationObj)) {
        if (__DEV__)
          console.error(
            'Search blocked: Origin and Destination City Codes or Station Codes are same',
          );
        return;
      }

      if (
        (tripType === TripType.ROUND_TRIP &&
          !isEmptyObject(originObj) &&
          !isEmptyObject(destinationObj) &&
          departDate &&
          returnDate) ||
        (tripType === TripType.ONE_WAY &&
          !isEmptyObject(originObj) &&
          !isEmptyObject(destinationObj) &&
          departDate)
      ) {
        sendSearchEvent();
        const carrierFilterOverrides = isAirAsiaFlightsOnly
          ? getDeepLinkCarrierFilterOverrides({
              isAirasiaFlightOnly: true,
            })
          : deepLinkAirlinesCarrierOverrides;
        const searchReturnDate =
          tripType === TripType.ROUND_TRIP ? returnDate : undefined;

        await searchMutateAsync({
          tripType: tripType,
          originObj: originObj,
          destinationObj: destinationObj,
          promoCode: promoCode,
          isAirAsiaFlightsOnly: isAirAsiaFlightsOnly,
          isSnapCheckbox: isSnapCheckbox,
          departDate: departDate,
          returnDate: searchReturnDate,
          adultCount: adultCount,
          childCount: childCount,
          infantCount: infantCount,
          cabinClass: cabinClass,
          didEditSearch: false,
          selectedDepartFlight: defaultSearchFlightFilters.selectedDepartFlight,
          selectedReturnFlight: defaultSearchFlightFilters.selectedReturnFlight,
          ...(isSmallScreen && !isListingScreen
            ? {
                ...resetListingPopularFilters,
                departPointsPlusCashEnabled: false,
                returnPointsPlusCashEnabled: false,
              }
            : {}),
          ...carrierFilterOverrides,
        });
        // Persist recent Searches
        await addRecentSearch.mutateAsync({
          origin: originObj,
          destination: destinationObj,
          departDate,
          returnDate: searchReturnDate,
          passengers: {
            adultCount,
            childCount,
            infantCount,
          },
          cabinClass,
          tripType,
        });

        if (
          Platform.OS !== 'web' &&
          NativeModules?.ReactToNative?.sendSearchHistory
        ) {
          const updatedRecentSearches =
            queryClient.getQueryData<RecentSearchesStore>(recentSearchesKey);
          const searchHistoryEntries = flattenRecentSearchesForHistory(
            updatedRecentSearches,
            { originObj, destinationObj, promoCode },
          );

          if (searchHistoryEntries.length) {
            NativeModules.ReactToNative.sendSearchHistory(
              'flight',
              searchHistoryEntries,
            );
          }
        }

        if (!isListingScreen && isSnapCheckbox === true) {
          const passengers = {
            adult: adultCount,
            child: childCount,
            infant: infantCount,
          };
          const snapDeepLink = snapQueryParams(
            config.env,
            passengers,
            stationOrCityCodeOrName(originObj as StationDataObject, 'code'),
            stationOrCityCodeOrName(
              destinationObj as StationDataObject,
              'code',
            ),
            departDate,
            searchReturnDate,
            config.locale ?? 'en-gb',
            flightConfig?.currency || config?.currency,
            promoCode,
            config.geoId ?? 'MY',
          );
          if (Platform.OS === 'web') {
            window.open(snapDeepLink, '_blank');
          } else {
            openModule({
              widget: superAppModules.SNAP_LISTING,
              deeplink: snapDeepLink,
            });
          }
        }
        // Below block to be executed on Android/iOS and web listing screen and web listing mobile view
        if ((isSmallScreen || isListingScreen) && !isHomeScreen) {
          const nextSearchListUrl = buildListingSearchUrl();

          if (
            nextSearchListUrl &&
            Platform.OS === 'web' &&
            typeof window !== 'undefined'
          ) {
            window.history.replaceState(
              window.history.state,
              '',
              nextSearchListUrl,
            );
          }

          setIsSearchFlightsButtonLoading(true);
          try {
            await searchAggregator.mutateAsync();
          } catch (error) {
            if (__DEV__) {
              console.log('❌ Listing search failed:', error);
            }
          } finally {
            setIsSearchFlightsButtonLoading(false);
          }
          if (props.controls.navigateToSearchList) {
            props.controls.navigateToSearchList(nextSearchListUrl);
          } else {
            onSearchPress?.();
          }
          return;
        }

        if (isHomeScreen) {
          setIsSearchFlightsButtonLoading(true);
          const result = await searchAggregator.mutateAsync({
            shouldCallFunnel: true,
            shouldCallAggregator: false,
            locale: resolveSearchLocale(
              (
                props.controls.deepLinkParams as
                  | FlightDeepLinkParams
                  | undefined
              )?.locale,
              config.locale,
            ),
          });

          const redirectionUrl = result?.funnel?.redirectionUrl;
          setIsSearchFlightsButtonLoading(false);
          if (redirectionUrl) {
            clearCheckoutAncillarySelections(queryClient);
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              window.location.assign(redirectionUrl);
            } else {
              props.controls.navigateToSearchList?.(redirectionUrl);
            }
          }

          return;
        }
      } else if (isEmptyObject(originObj)) {
        setIsOriginFieldError(ERROR_FIELD.ORIGIN_FIELD);
        logError(
          apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
          errorTrackingEvent({
            errorType: 'validation',
            errorDescription: 'Origin field validation error',
            errorMessage: t('flightsSearchPage.origin.originErrorMsg', {
              lng: DEFAULT_LOCALE,
            }),
            screenName: SCREEN_NAME.flightSearchWidget,
            service: '',
          }),
        );
      } else if (isEmptyObject(destinationObj)) {
        setIsDestinationFieldError(ERROR_FIELD.DESTINATION_FIELD);
        logError(
          apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
          errorTrackingEvent({
            errorType: 'validation',
            errorDescription: 'Destination field validation error',
            errorMessage: t(
              'flightsSearchPage.destination.destinationErrorMsg',
              { lng: DEFAULT_LOCALE },
            ),
            screenName: SCREEN_NAME.flightSearchWidget,
            service: '',
          }),
        );
      }
    } catch (error) {
      if (__DEV__) {
        console.log('❌ FSW handle Search failed:', error);
      }
      throw error;
    }
  };

  useEffect(() => {
    if (isFlightDealsSearchTriggered) {
      handleSearch();
      // Reset the trigger so it doesn't fire again on next render
      setIsFlightDealsSearchTriggered(false);
    }
  }, [isFlightDealsSearchTriggered]);

  const isOriginValid = !!originObj && !isEmptyObject(originObj);
  const isDestinationValid = !!destinationObj && !isEmptyObject(destinationObj);

  const showSnapCheckbox =
    tripType?.toUpperCase() === TripType.ROUND_TRIP &&
    isOriginValid &&
    isDestinationValid;

  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);

  useEffect(() => {
    if (shouldAutoSearch) {
      handleSearch();
      setShouldAutoSearch(false);
    }
  }, [
    tripType,
    departDate,
    returnDate,
    adultCount,
    childCount,
    infantCount,
    cabinClass,
    shouldAutoSearch,
  ]);

  return (
    <SafeAreaView style={{ flex: 1 }}>
      {props?.controls?.onPressHeaderBack || backHandler ? (
        <View style={styles.headerContainer}>
          <HeaderView
            headerStyle={HeaderStyle.STANDARD_SUB_TITLE}
            titleStart={translations.headerTitle}
            startIcon={{
              icon: {
                children: <ArrowBackPlatform />,
                height: 24,
                width: 24,
                tint: getColor(pd.sys.colour.content_primary),
              },
              onPress: () => {
                props?.controls?.onPressHeaderBack?.();
                if (backHandler) backHandler();
              },
            }}
          />
        </View>
      ) : null}
      <ScrollContainer
        {...(isMobileWidth
          ? {
              decelerationRate: Constants.SCROLL_SPEED,
              showsVerticalScrollIndicator: false,
              bounces: false,
              overScrollMode: 'never' as const,
            }
          : {})}
        style={styles.scrollView}
      >
        <View style={styles.subContainer}>
          {/* Journey Type */}

          {/* While unsettled the row is rendered invisibly (not omitted) so
              it is already in the server HTML and the widget does not grow
              by a row when it appears. */}
          <View
            style={[
              styles.tripContainer,
              isListingScreen && !isTripTypeSettled && styles.tripUnsettled,
            ]}
            pointerEvents={
              isListingScreen && !isTripTypeSettled ? 'none' : 'auto'
            }
          >
            <TripTypeSelectorView
              tripType={tripType}
              onChange={updateTripType}
              autoSearch={(nextTripType: TripType) => {
                const normalizedTripType =
                  resolveTripTypeFromParam(nextTripType);
                const isValidDates =
                  (normalizedTripType === TripType.ROUND_TRIP &&
                    departDate &&
                    returnDate) ||
                  (normalizedTripType === TripType.ONE_WAY && departDate);

                if (
                  normalizedTripType &&
                  isListingScreen &&
                  !isSmallScreen &&
                  !isEmptyObject(originObj) &&
                  !isEmptyObject(destinationObj) &&
                  isValidDates
                ) {
                  updateTripType(normalizedTripType);
                  setShouldAutoSearch(true);
                }
              }}
            />
          </View>

          {/* Below MarginTop is set from breakpoint md onwards */}
          {!isMobileWidth && <View style={{ marginTop: MARGIN_TOP }} />}

          <View style={styles.placeCalendarAndPaxPicker}>
            {/* Origin Destination PlacePicker */}
            <View
              style={[
                styles.originDestinationSelectorContainer,
                isListingScreen
                  ? styles.isListingOriginDestinationSelectorMaxWidth
                  : styles.originDestinationSelectorMaxWidth,
              ]}
            >
              <OriginDestinationSelectorView
                screenName={SCREEN_NAME.flightSearchWidget}
                originObj={originObj}
                destinationObj={destinationObj}
                updateOriginObj={(originObj) => setOriginObj(originObj)}
                updateDestinationObj={(newDestination, openDatePickerMenu) => {
                  setDestinationObj(newDestination);
                  if (
                    !isEmptyObject(newDestination) &&
                    !isSmallScreen &&
                    openDatePickerMenu &&
                    !isListingScreen
                  ) {
                    setShouldAutoSearchOnCalendarConfirm(true);
                    setAutoOpenDatePickerMenu(true);
                  }
                  if (
                    isListingScreen &&
                    !isHomeScreen &&
                    originObj &&
                    !isEmptyObject(originObj) &&
                    newDestination &&
                    !isEmptyObject(newDestination) &&
                    departDate
                  ) {
                    setShouldAutoSearch(true);
                  }
                }}
                originErrorField={isOriginFieldError}
                updateOriginErrorField={(val) => setIsOriginFieldError(val)}
                destinationErrorField={isDestinationFieldError}
                updateDestinationErrorField={(val) =>
                  setIsDestinationFieldError(val)
                }
                disableOrigin={uiDisable?.departureStation}
                disableArrival={uiDisable?.arrivalStation}
              />
            </View>
            <View style={styles.calendarAndPaxPicker}>
              <View style={styles.calendarContainer}>
                <CalendarView
                  screenName={SCREEN_NAME.flightSearchWidget}
                  tripType={tripType}
                  departDate={departDate}
                  returnDate={returnDate}
                  onChangeDepartDate={setDepartDate}
                  onChangeReturnDate={setReturnDate}
                  originStationObject={originObj}
                  destinationStationObject={destinationObj}
                  airlineProfile={funnel?.airlineProfile}
                  autoOpenDatePickerMenu={autoOpenDatePickerMenu}
                  setAutoOpenDatePickerMenu={(val) =>
                    setAutoOpenDatePickerMenu(val)
                  }
                  autoSearch={(dates) => {
                    if (
                      !isEmptyObject(dates) &&
                      isListingScreen &&
                      !isSmallScreen &&
                      !isEmptyObject(originObj) &&
                      !isEmptyObject(destinationObj)
                    ) {
                      setDepartDate(dates.departDate);
                      setReturnDate(
                        tripType === TripType.ROUND_TRIP
                          ? dates.returnDate
                          : undefined,
                      );
                      setShouldAutoSearch(true);
                    }
                  }}
                  shouldAutoSearchOnCalendarConfirm={
                    shouldAutoSearchOnCalendarConfirm
                  }
                  setShouldAutoSearchOnCalendarConfirm={
                    setShouldAutoSearchOnCalendarConfirm
                  }
                  departFlightSelected={
                    filters.selectedDepartFlight ? true : false
                  }
                />
              </View>

              <View
                style={[
                  styles.paxClassContainer,
                  isListingScreen
                    ? styles.isListingPaxClassContainerMaxWidth
                    : styles.paxContainerPaxClassMaxWidth,
                ]}
              >
                <PassengerClassFieldView
                  adultCount={adultCount}
                  updateAdultCount={setAdultCount}
                  childCount={childCount}
                  updateChildCount={setChildCount}
                  infantCount={infantCount}
                  updateInfantCount={setInfantCount}
                  cabinClass={cabinClass}
                  updateCabinClass={setCabinClass}
                  hideCabinClass={isDistress}
                  autoSearch={(paxAndCabinClass: SelectedValues) => {
                    if (
                      !isEmptyObject(paxAndCabinClass) &&
                      isListingScreen &&
                      !isSmallScreen &&
                      !isEmptyObject(originObj) &&
                      !isEmptyObject(destinationObj)
                    ) {
                      setAdultCount(paxAndCabinClass.adult);
                      setChildCount(paxAndCabinClass.child);
                      setInfantCount(paxAndCabinClass.infant);
                      setCabinClass(paxAndCabinClass.cabinClass);
                      setShouldAutoSearch(true);
                    }
                  }}
                />
              </View>

              {isListingScreen && (
                <View style={[styles.circleSearchIcon]}>
                  <ButtonView
                    buttonViewState={
                      isSearchFlightsButtonLoading
                        ? ButtonViewState.LOADING
                        : ButtonViewState.LOADED
                    }
                    text={t('flightsSearchPage.main.search')}
                    onPress={handleSearch}
                    type={ButtonViewType.PRIMARY_REGULAR}
                    disabled={false}
                  />
                </View>
              )}

              {!isListingScreen && !isMobileWidth && (
                <View style={[styles.circleSearchIcon]}>
                  <ButtonView
                    buttonViewState={
                      isSearchFlightsButtonLoading
                        ? ButtonViewState.LOADING
                        : ButtonViewState.LOADED
                    }
                    text={t('flightsSearchPage.main.search')}
                    onPress={handleSearch}
                    type={ButtonViewType.PRIMARY_REGULAR}
                    disabled={false}
                  />
                </View>
              )}
            </View>
          </View>

          {/*MARK: Checkboxes*/}
          {!isListingScreen && (
            <View style={styles.promoCodeAndCheckBoxContainer}>
              {/* Promo Code */}
              {!isDistress && !uiHide?.promo && (
                <View style={styles.promoCodeContainer}>
                  <PromoCodeView
                    promoCode={promoCode}
                    onChangeText={(text) => {
                      setPromoCode(text);
                    }}
                  />
                </View>
              )}

              {/* SNAP Checkbox */}
              {!isDistress && showSnapCheckbox && !uiHide?.snap ? (
                <View style={styles.snapCheckBoxContainer}>
                  <CheckboxView
                    isChecked={isSnapCheckbox}
                    label={translations.flightHotelCheckboxLabel}
                    labelHighlighter={
                      translations.flightHotelSaverBundleBadgeLabel
                    }
                    onPress={(state) => {
                      setIsSnapCheckbox(state);
                    }}
                  />
                </View>
              ) : null}

              {/* Search Button */}

              {isMobileWidth && (
                <View style={styles.searchButtonContainer}>
                  <ButtonView
                    buttonViewState={
                      isSearchFlightsButtonLoading
                        ? ButtonViewState.LOADING
                        : ButtonViewState.LOADED
                    }
                    disabled={false}
                    type={Constants.SEARCH_FLIGHTS_BUTTON_TYPE}
                    onPress={handleSearch}
                    text={t('flightsSearchPage.main.search')}
                  />
                </View>
              )}
            </View>
          )}
        </View>
        {!isWeb && !isDistress && !uiHide?.recentSearch ? (
          <View
            style={[
              styles.flightBannerContainer,
              styles.recentSearchesBannerContainer,
            ]}
          >
            <RecentSearchesCarouselView />
          </View>
        ) : null}
        {!isDistress && !uiHide?.flightRecommendation && (
          <View
            style={[
              styles.flightBannerContainer,
              styles.flightDealsBannerContainer,
            ]}
          >
            <FlightDealsCarouselView
              onPressCard={(selectedDeal) => {
                const {
                  tripType,
                  departDate,
                  returnDate,
                  adult,
                  child,
                  infant,
                  originObj,
                  destinationObj,
                } = selectedDeal;
                // Set Trip Type
                const selectedTripType =
                  tripType === 'R' ? TripType.ROUND_TRIP : TripType.ONE_WAY;
                updateTripType(selectedTripType);
                // Set Depart and return Station
                setOriginObj(originObj);
                setDestinationObj(destinationObj);

                // Set Depart and return date
                setDepartDate(new Date(departDate as string));
                setReturnDate(
                  selectedTripType === TripType.ROUND_TRIP
                    ? new Date(returnDate as string)
                    : undefined,
                );

                // Set Passenger
                setAdultCount(Number(adult ?? 1));
                setChildCount(Number(child ?? 0));
                setInfantCount(Number(infant ?? 0));
                setCabinClass(CabinClassType.ECONOMY);

                setPromoCode('');
                setIsSnapCheckbox(false);
                setIsAirAsiaFlightsOnly(false);

                // Turn on the trigger for handleSearch
                setIsFlightDealsSearchTriggered(true);
              }}
            />
          </View>
        )}
      </ScrollContainer>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create((pd) => ({
  scrollView: {
    flex: 1,
    backgroundColor: getColor(pd.sys.colour.container_none),
    borderRadius: 28,
  },
  flightBannerContainer: {
    display: { xs: 'flex', md: 'none' },
  },
  recentSearchesBannerContainer: {
    zIndex: 2,
  },
  flightDealsBannerContainer: {
    zIndex: 1,
  },
  headerContainer: {
    display: { xs: 'flex', md: 'none' },
  },
  subContainer: {
    paddingHorizontal: 16,
    paddingTop: {
      xs: Platform.OS === 'web' ? 8 : 16,
      md: 16,
    },
    paddingBottom: 16,
    zIndex: 10,
  },
  tripUnsettled: {
    opacity: 0,
  },
  tripContainer: {
    width: '100%',
    maxWidth: {
      xs: 'auto',
      md: 270,
      xl: 370,
    },
    zIndex: 3,
  },
  calendarContainer: {
    flex: 1,
    paddingLeft: {
      xl: 10,
    },
    marginTop: {
      xs: MARGIN_TOP,
      xl: 0,
    },
    maxWidth: {
      xs: 'auto',
    },
    zIndex: 3,
  },
  isListingOriginDestinationSelectorMaxWidth: {
    maxWidth: {
      xs: 'auto',
      xl: 440,
      superLarge: 489,
      tvLike: 'auto',
    },
  },
  originDestinationSelectorMaxWidth: {
    maxWidth: {
      xs: 'auto',
      xl: 460,
      superLarge: 500,
      tvLike: 'auto',
    },
  },
  originDestinationSelectorContainer: {
    flex: {
      lg: 1,
    },
    marginTop: {
      xs: MARGIN_TOP,
      md: 0,
    },
    zIndex: {
      sm: 2,
      xl: 1,
    },
  },
  snapCheckBoxContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    marginTop: {
      xs: MARGIN_TOP,
    },
    marginBottom: { xs: pd.ref.space.spacing30.px },
    zIndex: 2,
  },
  promoCodeAndCheckBoxContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      xs: 'flex-start',
      md: 'flex-start',
    },
    marginBottom: { md: pd.ref.space.spacing30.px },
    zIndex: 1,
  },
  airAsiaFlightsOnlyContainer: {
    marginTop: {
      xs: MARGIN_TOP,
    },
    marginLeft: {
      md: 30,
    },
    flexDirection: 'row',
  },
  promoCodeContainer: {
    width: { xs: '100%', md: 'auto' },
    marginTop: {
      xs: MARGIN_TOP,
    },
    marginRight: {
      md: MARGIN_RIGHT,
    },
    zIndex: 3,
  },
  paxClassContainer: {
    paddingLeft: {
      md: 10,
    },
    marginTop: {
      xs: MARGIN_TOP,
      xl: 0,
    },
    zIndex: 2,
  },
  isListingPaxClassContainerMaxWidth: {
    maxWidth: {
      xl: 250,
      tvLike: 'auto',
    },
  },
  paxContainerPaxClassMaxWidth: {
    maxWidth: {
      xl: 237,
      superLarge: 242,
      tvLike: 'auto',
    },
  },
  circleSearchIcon: {
    paddingLeft: {
      md: 10,
    },
    top: {
      md: 14,
      xl: 0,
    },
    zIndex: 1,
  },
  searchButtonContainer: {
    marginTop: {
      xs: MARGIN_TOP,
    },
    width: {
      xs: '100%',
      md: 'auto',
    },
    marginLeft: {
      xs: 0,
      md: 'auto',
    },
    zIndex: 1,
  },
  placeCalendarAndPaxPicker: {
    flex: 1,
    flexDirection: {
      xs: 'column',
      xl: 'row',
    },
    zIndex: 2,
  },
  calendarAndPaxPicker: {
    flex: 1,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    zIndex: {
      sm: 1,
      xl: 2,
    },
  },
}));

export default FlightSearchWidget;
